import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { IMG } from "../../data/site";

const LINKS = [
  { label: "Vehicles", href: "#vehicles" },
  { label: "Since 2002", href: "#legacy" },
  { label: "Service", href: "#service" },
  { label: "Locations", href: "#locations" },
];

export const Navbar = ({ onNav, topOffset = 0 }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    onNav?.(href);
  };

  return (
    <header
      data-testid="site-navbar"
      style={{ top: topOffset }}
      className={`fixed inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" }))} className="flex items-baseline gap-2" data-testid="nav-logo">
            <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight uppercase text-white">Sasya</span>
            <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight uppercase text-red-600">Hero</span>
          </button>
          <span className="hidden sm:block h-6 w-px bg-white/20" />
          <div className="hidden sm:flex items-center gap-2" data-testid="nav-brand-logos">
            <div className="bg-white rounded px-2 py-1 flex items-center"><img src={IMG.heroLogo} alt="Hero MotoCorp" className="h-6 md:h-7 w-auto object-contain" /></div>
            <div className="bg-white rounded px-2 py-1 flex items-center"><img src={IMG.vidaLogo} alt="VIDA" className="h-6 md:h-7 w-auto object-contain" /></div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium tracking-wide text-zinc-300 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-red-600 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:9281452805"
            data-testid="nav-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white"
          >
            <Phone size={15} /> 92814 52805
          </a>
          <button
            onClick={() => go("#service")}
            data-testid="nav-book-service-btn"
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold uppercase tracking-wide px-5 py-2.5 transition-colors"
          >
            Book Service
          </button>
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-lg font-semibold text-zinc-200" data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#service")} className="mt-2 bg-red-600 text-white font-bold uppercase py-3" data-testid="mnav-book-service">
                Book Service
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
